import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { CheckCircle2 } from "lucide-react";
import { RecordPanel } from "./RecordFrame";
import { CopyButton } from "./ReachItem";
import { visiblePeople } from "./recordFormat";

export interface RailPerson {
  id: string; name: string; title?: string | null; email?: string | null; phone?: string | null;
  linkedinUrl?: string | null; isPrimary?: boolean | null; engaged?: boolean | null;
  willingToAdvocate?: boolean | null;
}

/**
 * People at the record's company: six, then "Show all". Every email and phone
 * copies. A company page adds an Add button and per-person extras (the
 * suppression badge and Apollo enrich).
 */
export function RailPeople({ title, people, emptyText = "Nobody else here yet.", action, rowExtras }: {
  title: string; people: RailPerson[]; emptyText?: string; action?: ReactNode; rowExtras?: (p: RailPerson) => ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  const shown = visiblePeople(people, showAll);
  return (
    <RecordPanel title={title} count={people.length} action={action}>
      {people.length === 0 && <p className="text-[12.5px] text-muted-foreground">{emptyText}</p>}
      <ul className="divide-y">
        {shown.map((p) => (
          <li key={p.id} className="group grid gap-px py-1.5">
            <div className="flex min-w-0 items-center gap-1.5">
              <Link href={`/contacts/${p.id}`} className="truncate text-[13px] font-semibold hover:underline">{p.name}</Link>
              {p.engaged && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="Spoken with" />}
              {p.isPrimary && <span className="rounded bg-primary/15 px-1 text-[10px] font-bold uppercase tracking-wide text-primary-text">Primary</span>}
              {p.willingToAdvocate && <span className="rounded bg-amber-100 px-1 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-950/40 dark:text-amber-400">Advocate</span>}
              {rowExtras && <span className="ml-auto flex shrink-0 items-center gap-1">{rowExtras(p)}</span>}
            </div>
            {p.title && <div className="truncate text-xs text-muted-foreground">{p.title}</div>}
            {(p.email || p.phone) && (
              <div className="flex flex-wrap gap-x-2.5 text-xs text-muted-foreground">
                {p.email && <span className="inline-flex min-w-0 items-center gap-0.5 tabular-nums"><span className="truncate">{p.email}</span><CopyButton value={p.email} /></span>}
                {p.phone && <span className="inline-flex items-center gap-0.5 tabular-nums">{p.phone}<CopyButton value={p.phone} /></span>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {people.length > shown.length && (
        <button type="button" onClick={() => setShowAll(true)} className="mt-1.5 text-xs font-semibold text-primary-text hover:underline">
          Show all {people.length}
        </button>
      )}
    </RecordPanel>
  );
}
